/**
 * Searchable IANA timezone combobox (spec 2026-07-26 §1). Mirrors the
 * STTLanguagePicker pattern: Popover + combobox trigger + filter input +
 * grouped listbox. Zone list comes from Intl.supportedValuesOf('timeZone');
 * when that's unavailable the component degrades to the plain text input
 * this field used to be, so the tab never loses the field.
 */

import { useId, useMemo, useState } from 'react'
import { Check, ChevronsUpDown, LocateFixed, X } from 'lucide-react'
import { cn } from '@/utils'
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from '@/components/ui'

export interface TimezonePickerProps {
  value: string | null
  onChange: (tz: string | null) => void
  disabled?: boolean
  className?: string
  id?: string
}

// Usage-weighted, not alphabetical — America first for our deployments.
const REGION_ORDER = [
  'America', 'Europe', 'Asia', 'Africa', 'Pacific', 'Australia',
  'Atlantic', 'Indian', 'Antarctica', 'Other',
] as const

function supportedZones(): string[] | null {
  if (typeof Intl.supportedValuesOf !== 'function') return null
  return Intl.supportedValuesOf('timeZone')
}

/** "mexico city" matches "America/Mexico_City" — underscores ≡ spaces. */
function normalizeQuery(s: string): string {
  return s.toLowerCase().replace(/[_\s]+/g, ' ')
}

function regionOf(zone: string): string {
  const region = zone.split('/')[0]
  return zone.includes('/') && REGION_ORDER.includes(region as (typeof REGION_ORDER)[number])
    ? region
    : 'Other'
}

/** "America/Argentina/Buenos_Aires" → "Argentina / Buenos Aires". */
function cityLabel(zone: string): string {
  if (!zone.includes('/')) return zone
  return zone.split('/').slice(1).join(' / ').replace(/_/g, ' ')
}

function offsetLabel(zone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? null
  } catch {
    return null
  }
}

export function TimezonePicker({ value, onChange, disabled, className, id }: TimezonePickerProps) {
  const [open, setOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const [search, setSearch] = useState('')
  const listboxId = useId()

  const zones = useMemo(supportedZones, [])

  // Offsets are computed once, lazily on first open, and cached for the
  // component's lifetime (spec: drift across a DST boundary mid-session is
  // acceptable). ~400 Intl.DateTimeFormat constructions — only on demand.
  const offsets = useMemo<Record<string, string | null>>(() => {
    if (!hasOpened || !zones) return {}
    return Object.fromEntries(zones.map((z) => [z, offsetLabel(z)]))
  }, [hasOpened, zones])

  const filtered = useMemo(() => {
    if (!zones) return []
    const q = normalizeQuery(search.trim())
    if (!q) return zones
    return zones.filter((z) => normalizeQuery(z).includes(q))
  }, [zones, search])

  const grouped = useMemo(() => {
    const groups = new Map<string, string[]>()
    for (const zone of filtered) {
      const region = regionOf(zone)
      const list = groups.get(region)
      if (list) list.push(zone)
      else groups.set(region, [zone])
    }
    return REGION_ORDER.filter((r) => groups.has(r)).map(
      (r) => [r, groups.get(r)!] as const,
    )
  }, [filtered])

  // Fallback for browsers without Intl.supportedValuesOf: the plain text
  // input this field always was.
  if (!zones) {
    return (
      <Input
        id={id}
        aria-label="Timezone"
        placeholder="America/Mexico_City"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className={className}
      />
    )
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) setHasOpened(true)
    else setSearch('')
  }

  const handleSelect = (tz: string | null) => {
    onChange(tz)
    setOpen(false)
    setSearch('')
  }

  const myTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const renderOption = (zone: string) => (
    <button
      key={zone}
      type="button"
      role="option"
      aria-selected={value === zone}
      onClick={() => handleSelect(zone)}
      className={cn(
        'flex w-full items-center px-3 py-1.5 text-sm text-left',
        'hover:bg-accent focus:outline-hidden focus:bg-accent',
        value === zone && 'font-medium',
      )}
    >
      <Check
        className={cn(
          'mr-2 h-4 w-4 shrink-0',
          value === zone ? 'opacity-100 text-primary' : 'opacity-0',
        )}
      />
      <span className="flex-1 truncate">{cityLabel(zone)}</span>
      {offsets[zone] && (
        <span className="ml-2 shrink-0 text-xs text-muted-foreground">
          {offsets[zone]}
        </span>
      )}
    </button>
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          role="combobox"
          type="button"
          variant="secondary"
          aria-label="Timezone"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          disabled={disabled}
          className={cn('w-full justify-between font-normal border border-border', className)}
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value ?? 'Select timezone…'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-(--radix-popover-trigger-width) p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          id={listboxId}
          role="listbox"
          className="rounded-md"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false)
              setSearch('')
            }
          }}
        >
          <div className="p-2 border-b border-border">
            <Input
              autoFocus
              placeholder="Search timezones…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {!search.trim() && (
              <div role="group" aria-label="Quick actions" className="border-b border-border pb-1 mb-1">
                <button
                  type="button"
                  role="option"
                  aria-selected={value === myTimezone}
                  onClick={() => handleSelect(myTimezone)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left text-primary hover:bg-accent focus:outline-hidden focus:bg-accent"
                >
                  <LocateFixed className="h-4 w-4 shrink-0" />
                  Use my timezone
                  <span className="text-xs text-muted-foreground truncate">{myTimezone}</span>
                </button>
                {value !== null && (
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => handleSelect(null)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left text-muted-foreground hover:bg-accent focus:outline-hidden focus:bg-accent"
                  >
                    <X className="h-4 w-4 shrink-0" />
                    Clear
                  </button>
                )}
              </div>
            )}

            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-center text-muted-foreground">
                No timezone found.
              </div>
            )}

            {grouped.map(([region, regionZones]) => (
              <div key={region} role="group" aria-label={region}>
                <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide select-none">
                  {region}
                </div>
                {regionZones.map(renderOption)}
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
